import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useCaptableSignUp } from "../../queries/captableLogin";
import {
  clearCredentials,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import { SignUpModel } from "../../types/CaptableLoginModel";
import { Input, Label } from "../../components/shared/InputField";
import { loginPageImageUrl } from "../../constants/LoginPageImage";
import { useAuthorizationStore, useAuthStore } from "../../store";
import useIsMobile from "../../utils/detectDevice";
import {
  ButtonSize,
  PrimaryCTAButton,
} from "../quickRound/CTAButtonComponents";

const signUpSchema = Yup.object().shape({
  email: Yup.string()
    .email("Wrong email format")
    .required("Please enter valid email"),
  password: Yup.string().required("Password is required"),
  confirmPassword: Yup.string().required("Confirm Password is required"),
  fullName: Yup.string().required("Name is required"),
});

const initialValues: SignUpModel = {
  email: "",
  password: "",
  confirmPassword: "",
  fullName: "",
};

export default function CaptableSignUpPage() {
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { mutate: captableSignUp } = useCaptableSignUp();

  const imageUrl = loginPageImageUrl;
  const length = 3;
  let count = 0;
  const { clearAuthorization } = useAuthorizationStore();

  useEffect(() => {
    //timer to change the sign-up image on right side
    const intervalId = setInterval(() => {
      count++;
      setActiveIndex(count % length);
    }, 5000);
    return () => clearInterval(intervalId);
  }, []);

  const { isMobile } = useIsMobile(1025);

  return (
    <>
      {!isMobile ? (
        <div className="flex h-screen bg-white w-full">
          <div className="flex flex-col justify-between flex-1 px-4 pb-12 mx-auto sm:px-6 lg:flex-none  md:w-5/12 w-full">
            <div className="pt-[32px] ">
              <img
                className="h-12 mx-auto w-28 mb-2"
                src={
                  "https://equion-dev.s3.us-west-2.amazonaws.com/hissa-logo-dark.png"
                }
                alt="hissa_logo"
              />
            </div>
            <div className="w-full max-w-sm mx-auto lg:w-96">
              <h2 className="text-center text-[28px] font-semibold tracking-tight text-[#181C32]">
                Register User
              </h2>

              <div className="mt-8">
                <div className="mt-6">
                  <Formik
                    enableReinitialize={true}
                    validationSchema={signUpSchema}
                    initialValues={initialValues}
                    onSubmit={(values) => {
                      setLoading(true);
                      clearAuthorization();
                      clearCredentials();
                      captableSignUp(
                        {
                          email: values.email,
                          password: values.password,
                          confirmPassword: values.confirmPassword,
                          fullName: values.fullName,
                        },
                        {
                          onSuccess: (data) => {
                            setLoading(false);
                            toast("User registered successfully", {
                              type: "success",
                              autoClose: 2000,
                            });
                            if (data.data.userId) {
                              navigate("/login");
                            }
                          },
                          onError: (err: any) => {
                            setLoading(false);
                            toast(err.response.data.errors[0], {
                              type: "error",
                              autoClose: 2000,
                            });
                          },
                        }
                      );
                    }}
                  >
                    {(formik) => (
                      <Form>
                        <VStack className="gap-4">
                          <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              type="email"
                              autoComplete="email"
                              {...formik.getFieldProps(`email`)}
                            />
                            {formik.touched?.email && formik.errors?.email && (
                              <Error text={formik.errors?.email} />
                            )}
                          </div>
                          <div>
                            <Label htmlFor="email">Name</Label>
                            <Input
                              id="fullName"
                              type="text"
                              autoComplete="text"
                              {...formik.getFieldProps(`fullName`)}
                            />
                            {formik.touched?.fullName &&
                              formik.errors?.fullName && (
                                <Error text={formik.errors?.fullName} />
                              )}
                          </div>
                          <div>
                            <Label htmlFor="password">Password</Label>
                            <Input
                              id="password"
                              type={`${
                                showConfirmPassword ? "text" : "password"
                              }`}
                              autoComplete="current-password"
                              {...formik.getFieldProps(`password`)}
                            />

                            {formik.touched?.password &&
                              formik.errors?.password && (
                                <Error text={formik.errors?.password} />
                              )}
                          </div>
                          <div>
                            <Label htmlFor="password">Confirm Password</Label>

                            <Input
                              id="confirmPassword"
                              type={`${
                                showConfirmPassword ? "text" : "password"
                              }`}
                              autoComplete="current-password"
                              {...formik.getFieldProps(`confirmPassword`)}
                            />

                            {formik.touched?.password &&
                              formik.errors?.password && (
                                <Error text={formik.errors?.password} />
                              )}
                          </div>
                          <div className="relative flex items-start">
                            <div className="flex h-6 items-center">
                              <input
                                id="comments"
                                aria-describedby="comments-description"
                                name="comments"
                                type="checkbox"
                                onClick={() => {
                                  setShowConfirmPassword(!showConfirmPassword);
                                }}
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor="comments"
                                className="font-medium text-gray-900"
                              >
                                Show Password
                              </label>
                            </div>
                          </div>
                          <div className="flex items-center justify-start">
                            <div className="text-sm font-semibold text-[#181C32] cursor-pointer">
                              Already User ?{" "}
                              <span
                                onClick={() => {
                                  navigate("/login");
                                }}
                                data-event-name="Click here to sign in"
                                className=" text-blue-700 cursor-pointer underline"
                              >
                                Click here to sign in
                              </span>
                            </div>
                          </div>
                          <div className="mx-auto mt-16">
                            <PrimaryCTAButton
                              type="submit"
                              event-name="Sign up button"
                              buttonSize={ButtonSize.MEDIUM}
                              loading={loading}
                            >
                              Sign Up
                            </PrimaryCTAButton>
                          </div>
                        </VStack>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
            <div></div>
          </div>
          <VStack className="md:w-7/12 md:visible invisible bg-gradient-to-tl from-[#030093] to-[#2E4D9B]/[.54] justify-evenly">
            <VStack className="gap-8">
              <img
                className="inset-0 md:visible invisible object-cover mx-auto w-fit px-32"
                src={imageUrl[activeIndex]}
                alt=""
              />

              {activeIndex === 0 ? (
                <div className="px-10 pb-10 text-2xl font-semibold text-center text-white">
                  Elevate your equity management with{" "}
                  <span className="text-orange-501">Hissa™</span> !
                </div>
              ) : activeIndex === 1 ? (
                <div className="px-10 pb-10 text-2xl font-semibold text-center text-white">
                  <span className="text-orange-501">Hissa™</span> takes the
                  chaos out of the Cap Table!
                </div>
              ) : (
                <div className="px-10 pb-10 text-2xl font-semibold text-center text-white">
                  Value creation through Stock Options just got easier with{" "}
                  <span className="text-orange-501">Hissa™</span> !
                </div>
              )}
            </VStack>
          </VStack>
        </div>
      ) : (
        <div className="flex h-screen bg-white w-full">
          <div className="flex flex-col justify-between flex-1 px-4 pb-12 mx-auto sm:px-6 lg:flex-none w-full">
            <div className="pt-[32px] ">
              <img
                className="h-12 mx-auto w-28 mb-2"
                src={
                  "https://equion-dev.s3.us-west-2.amazonaws.com/hissa-logo-dark.png"
                }
                alt="hissa_logo"
              />
            </div>
            <div className="w-full max-w-sm lg:w-96 border border-gray-200 px-4 py-8 rounded-md shadow-box mx-auto">
              <h2 className="text-center text-[28px] font-semibold tracking-tight text-[#181C32]">
                Sign In
              </h2>

              <div className="mt-8">
                <div className="mt-6">
                  <Formik
                    enableReinitialize={true}
                    validationSchema={signUpSchema}
                    initialValues={initialValues}
                    onSubmit={(values) => {
                      setLoading(true);
                      clearCredentials();
                      captableSignUp(
                        {
                          email: values.email,
                          password: values.password,
                          confirmPassword: values.confirmPassword,
                          fullName: values.fullName,
                        },
                        {
                          onSuccess: (data) => {
                            setLoading(false);
                            toast("User registered Successfully!", {
                              type: "success",
                              autoClose: 2000,
                            });
                            if (data.data.userId) {
                              navigate("/login");
                            }
                          },
                          onError: (err: any) => {
                            setLoading(false);
                            toast(err.response.data.errors[0], {
                              type: "error",
                              autoClose: 2000,
                            });
                          },
                        }
                      );
                    }}
                  >
                    {(formik) => (
                      <Form>
                        <VStack className="gap-4">
                          <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              type="email"
                              autoComplete="email"
                              {...formik.getFieldProps(`email`)}
                            />
                            {formik.touched?.email && formik.errors?.email && (
                              <Error text={formik.errors?.email} />
                            )}
                          </div>
                          <div>
                            <Label htmlFor="email">Name</Label>
                            <Input
                              id="fullName"
                              type="text"
                              autoComplete="text"
                              {...formik.getFieldProps(`fullName`)}
                            />
                            {formik.touched?.fullName &&
                              formik.errors?.fullName && (
                                <Error text={formik.errors?.fullName} />
                              )}
                          </div>
                          <div>
                            <Label htmlFor="password">Password</Label>

                            <Input
                              id="password"
                              type={`${
                                showConfirmPassword ? "text" : "password"
                              }`}
                              autoComplete="current-password"
                              {...formik.getFieldProps(`password`)}
                            />

                            {formik.touched?.password &&
                              formik.errors?.password && (
                                <Error text={formik.errors?.password} />
                              )}
                          </div>
                          <div>
                            <Label htmlFor="password">Confirm Password</Label>

                            <Input
                              id="confirmPassword"
                              type={`${
                                showConfirmPassword ? "text" : "password"
                              }`}
                              autoComplete="current-password"
                              {...formik.getFieldProps(`confirmPassword`)}
                            />

                            {formik.touched?.password &&
                              formik.errors?.password && (
                                <Error text={formik.errors?.password} />
                              )}
                          </div>
                          <div className="relative flex items-start">
                            <div className="flex h-6 items-center">
                              <input
                                id="comments"
                                aria-describedby="comments-description"
                                name="comments"
                                type="checkbox"
                                onClick={() => {
                                  setShowConfirmPassword(!showConfirmPassword);
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor="comments"
                                className="font-medium text-gray-900"
                              >
                                Show Password
                              </label>
                            </div>
                          </div>
                          <div className="flex items-center justify-start">
                            <div className="text-sm font-semibold text-[#181C32] cursor-pointer">
                              Already User ?{" "}
                              <span
                                data-event-name="Click here to sign in"
                                onClick={() => {
                                  navigate("/login");
                                }}
                                className=" text-blue-700 cursor-pointer underline"
                              >
                                Click here to sign in
                              </span>
                            </div>
                          </div>
                          <div className="mx-auto mt-16">
                            <PrimaryCTAButton
                              event-name="Sign up button"
                              buttonSize={ButtonSize.MEDIUM}
                              type="submit"
                              loading={loading}
                            >
                              Sign Up
                            </PrimaryCTAButton>
                          </div>
                        </VStack>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
            <div></div>
          </div>
        </div>
      )}
    </>
  );
}
